import type { Spell } from '@character-creator/types';

export const level2Spells: Spell[] = [
  {
    id: 'aid',
    name: 'Aid',
    level: 2,
    school: 'abjuration',
    ritual: false,
    castingTime: '1 action',
    range: '30 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a tiny strip of white cloth' },
    duration: '8 hours',
    concentration: false,
    description: "Your spell bolsters your allies with toughness and resolve. Choose up to three creatures within range. Each target's hit point maximum and current hit points increase by 5 for the duration.",
    higherLevel: "When you cast this spell using a spell slot of 3rd level or higher, a target's hit points increase by an additional 5 for each slot level above 2nd.",
    classes: ['cleric', 'paladin'],
    source: 'srd-5.1',
  },
  {
    id: 'alter-self',
    name: 'Alter Self',
    level: 2,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: 'Self',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 hour',
    concentration: true,
    description: 'You assume a different form. When you cast the spell, choose one of the following options. Aquatic Adaptation: You adapt your body to an aquatic environment. You can breathe underwater and gain a swimming speed equal to your walking speed. Change Appearance: You transform your appearance. Natural Weapons: You grow claws, fangs, spines, horns, or a different natural weapon of your choice.',
    classes: ['sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'animal-messenger',
    name: 'Animal Messenger',
    level: 2,
    school: 'enchantment',
    ritual: true,
    castingTime: '1 action',
    range: '30 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a morsel of food' },
    duration: '24 hours',
    concentration: false,
    description: "By means of this spell, you use an animal to deliver a message. Choose a Tiny beast you can see within range, such as a squirrel, a blue jay, or a bat. You specify a location, which you must have visited, and a recipient who matches a general description. The beast travels for the duration toward the specified location, covering about 50 miles per 24 hours or 25 miles for a slower beast.",
    higherLevel: "If you cast this spell using a spell slot of 3rd level or higher, the duration of the spell increases by 48 hours for each slot level above 2nd.",
    classes: ['bard', 'druid', 'ranger'],
    source: 'srd-5.1',
  },
  {
    id: 'arcane-lock',
    name: 'Arcane Lock',
    level: 2,
    school: 'abjuration',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'gold dust worth at least 25 gp, which the spell consumes', materialCost: 25, materialConsumed: true },
    duration: 'Until dispelled',
    concentration: false,
    description: "You touch a closed door, window, gate, chest, or other entryway, and it becomes locked for the duration. You and the creatures you designate when you cast this spell can open the object normally. You can also set a password that, when spoken within 5 feet of the object, suppresses this spell for 1 minute. Otherwise, it is impassable until it is broken or the spell is dispelled or suppressed. Casting knock on the object suppresses arcane lock for 10 minutes.",
    classes: ['wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'augury',
    name: 'Augury',
    level: 2,
    school: 'divination',
    ritual: true,
    castingTime: '1 minute',
    range: 'Self',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'specially marked sticks, bones, or similar tokens worth at least 25 gp', materialCost: 25 },
    duration: 'Instantaneous',
    concentration: false,
    description: "By casting gem-inlaid sticks, rolling dragon bones, laying out ornate cards, or employing some other divining tool, you receive an omen from an otherworldly entity about the results of a specific course of action that you plan to take within the next 30 minutes. The DM chooses from the following possible omens: Weal, Woe, Weal and woe, or Nothing.",
    classes: ['cleric'],
    source: 'srd-5.1',
  },
  {
    id: 'barkskin',
    name: 'Barkskin',
    level: 2,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a handful of oak bark' },
    duration: '1 hour',
    concentration: true,
    description: "You touch a willing creature. Until the spell ends, the target's skin has a rough, bark-like appearance, and the target's AC can't be less than 16, regardless of what kind of armor it is wearing.",
    classes: ['druid', 'ranger'],
    source: 'srd-5.1',
  },
  {
    id: 'blindness-deafness',
    name: 'Blindness/Deafness',
    level: 2,
    school: 'necromancy',
    ritual: false,
    castingTime: '1 action',
    range: '30 feet',
    components: { verbal: true, somatic: false, material: false },
    duration: '1 minute',
    concentration: false,
    description: "You can blind or deafen a foe. Choose one creature that you can see within range to make a Constitution saving throw. If it fails, the target is either blinded or deafened (your choice) for the duration. At the end of each of its turns, the target can make a Constitution saving throw. On a success, the spell ends.",
    higherLevel: "When you cast this spell using a spell slot of 3rd level or higher, you can target one additional creature for each slot level above 2nd.",
    classes: ['bard', 'cleric', 'sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'blur',
    name: 'Blur',
    level: 2,
    school: 'illusion',
    ritual: false,
    castingTime: '1 action',
    range: 'Self',
    components: { verbal: true, somatic: false, material: false },
    duration: '1 minute',
    concentration: true,
    description: "Your body becomes blurred, shifting and wavering to all who can see you. For the duration, any creature has disadvantage on attack rolls against you. An attacker is immune to this effect if it doesn't rely on sight, as with blindsight, or can see through illusions, as with truesight.",
    classes: ['sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'branding-smite',
    name: 'Branding Smite',
    level: 2,
    school: 'evocation',
    ritual: false,
    castingTime: '1 bonus action',
    range: 'Self',
    components: { verbal: true, somatic: false, material: false },
    duration: '1 minute',
    concentration: true,
    description: "The next time you hit a creature with a weapon attack before this spell ends, the weapon gleams with astral radiance as you strike. The attack deals an extra 2d6 radiant damage to the target, which becomes visible if it's invisible, and the target sheds dim light in a 5-foot radius and can't become invisible until the spell ends.",
    higherLevel: "When you cast this spell using a spell slot of 3rd level or higher, the extra damage increases by 1d6 for each slot level above 2nd.",
    classes: ['paladin'],
    source: 'srd-5.1',
  },
  {
    id: 'calm-emotions',
    name: 'Calm Emotions',
    level: 2,
    school: 'enchantment',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 minute',
    concentration: true,
    description: "You attempt to suppress strong emotions in a group of people. Each humanoid in a 20-foot-radius sphere centered on a point you choose within range must make a Charisma saving throw; a creature can choose to fail this saving throw if it wishes. If a creature fails its saving throw, choose one of the following two effects: suppress any effect causing a target to be charmed or frightened, or make a target indifferent about creatures of your choice that it is hostile toward.",
    classes: ['bard', 'cleric'],
    source: 'srd-5.1',
  },
  {
    id: 'continual-flame',
    name: 'Continual Flame',
    level: 2,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'ruby dust worth 50 gp, which the spell consumes', materialCost: 50, materialConsumed: true },
    duration: 'Until dispelled',
    concentration: false,
    description: "A flame, equivalent in brightness to a torch, springs forth from an object that you touch. The effect looks like a regular flame, but it creates no heat and doesn't use oxygen. A continual flame can be covered or hidden but not smothered or quenched.",
    classes: ['cleric', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'darkness',
    name: 'Darkness',
    level: 2,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: false, material: true, materialDescription: 'bat fur and a drop of pitch or piece of coal' },
    duration: '10 minutes',
    concentration: true,
    description: "Magical darkness spreads from a point you choose within range to fill a 15-foot-radius sphere for the duration. The darkness spreads around corners. A creature with darkvision can't see through this darkness, and nonmagical light can't illuminate it. If the point you choose is on an object you are holding or one that isn't being worn or carried, the darkness emanates from the object and moves with it.",
    classes: ['sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'darkvision',
    name: 'Darkvision',
    level: 2,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'either a pinch of dried carrot or an agate' },
    duration: '8 hours',
    concentration: false,
    description: "You touch a willing creature to grant it the ability to see in the dark. For the duration, that creature has darkvision out to a range of 60 feet.",
    classes: ['druid', 'ranger', 'sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'detect-thoughts',
    name: 'Detect Thoughts',
    level: 2,
    school: 'divination',
    ritual: false,
    castingTime: '1 action',
    range: 'Self',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a copper piece' },
    duration: '1 minute',
    concentration: true,
    description: "For the duration, you can read the thoughts of certain creatures. When you cast the spell and as your action on each turn until the spell ends, you can focus your mind on any one creature that you can see within 30 feet of you. If the creature you choose has an Intelligence of 3 or lower or doesn't speak any language, the creature is unaffected.",
    classes: ['bard', 'sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'enhance-ability',
    name: 'Enhance Ability',
    level: 2,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'fur or a feather from a beast' },
    duration: '1 hour',
    concentration: true,
    description: "You touch a creature and bestow upon it a magical enhancement. Choose one of the following effects; the target gains that effect until the spell ends: Bear's Endurance, Bull's Strength, Cat's Grace, Eagle's Splendor, Fox's Cunning, or Owl's Wisdom.",
    higherLevel: "When you cast this spell using a spell slot of 3rd level or higher, you can target one additional creature for each slot level above 2nd.",
    classes: ['bard', 'cleric', 'druid', 'sorcerer'],
    source: 'srd-5.1',
  },
  {
    id: 'enlarge-reduce',
    name: 'Enlarge/Reduce',
    level: 2,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: '30 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a pinch of powdered iron' },
    duration: '1 minute',
    concentration: true,
    description: "You cause a creature or an object you can see within range to grow larger or smaller for the duration. Choose either a creature or an object that is neither worn nor carried. If the target is unwilling, it can make a Constitution saving throw. On a success, the spell has no effect. Enlarge: The target's size doubles and its weight multiplies by eight. Reduce: The target's size is halved and its weight is reduced to one-eighth.",
    classes: ['sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'enthrall',
    name: 'Enthrall',
    level: 2,
    school: 'enchantment',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 minute',
    concentration: false,
    description: "You weave a distracting string of words, causing creatures of your choice that you can see within range and that can hear you to make a Wisdom saving throw. Any creature that can't be charmed succeeds on this saving throw automatically, and if you or your companions are fighting a creature, it has advantage on the save. On a failed save, the target has disadvantage on Wisdom (Perception) checks made to perceive any creature other than you until the spell ends or until the target can no longer hear you.",
    classes: ['bard', 'warlock'],
    source: 'srd-5.1',
  },
  {
    id: 'find-steed',
    name: 'Find Steed',
    level: 2,
    school: 'conjuration',
    ritual: false,
    castingTime: '10 minutes',
    range: '30 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: 'Instantaneous',
    concentration: false,
    description: "You summon a spirit that assumes the form of an unusually intelligent, strong, and loyal steed, creating a long-lasting bond with it. Appearing in an unoccupied space within range, the steed takes on a form that you choose: a warhorse, a pony, a camel, an elk, or a mastiff. The steed has the statistics of the chosen form, though it is a celestial, fey, or fiend (your choice) instead of its normal type.",
    classes: ['paladin'],
    source: 'srd-5.1',
  },
  {
    id: 'find-traps',
    name: 'Find Traps',
    level: 2,
    school: 'divination',
    ritual: false,
    castingTime: '1 action',
    range: '120 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: 'Instantaneous',
    concentration: false,
    description: "You sense the presence of any trap within range that is within line of sight. A trap, for the purpose of this spell, includes anything that would inflict a sudden or unexpected effect you consider harmful or undesirable, which was specifically intended as such by its creator. Thus, the spell would sense an area affected by the alarm spell, a glyph of warding, or a mechanical pit trap, but it would not reveal a natural weakness in the floor.",
    classes: ['cleric', 'druid', 'ranger'],
    source: 'srd-5.1',
  },
  {
    id: 'flame-blade',
    name: 'Flame Blade',
    level: 2,
    school: 'evocation',
    ritual: false,
    castingTime: '1 bonus action',
    range: 'Self',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'leaf of sumac' },
    duration: '10 minutes',
    concentration: true,
    description: "You evoke a fiery blade in your free hand. The blade is similar in size and shape to a scimitar, and it lasts for the duration. If you let go of the blade, it disappears, but you can evoke the blade again as a bonus action. You can use your action to make a melee spell attack with the fiery blade. On a hit, the target takes 3d6 fire damage. The flaming blade sheds bright light in a 10-foot radius and dim light for an additional 10 feet.",
    higherLevel: "When you cast this spell using a spell slot of 4th level or higher, the damage increases by 1d6 for every two slot levels above 2nd.",
    classes: ['druid'],
    source: 'srd-5.1',
  },
  {
    id: 'flaming-sphere',
    name: 'Flaming Sphere',
    level: 2,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a bit of tallow, a pinch of brimstone, and a dusting of powdered iron' },
    duration: '1 minute',
    concentration: true,
    description: "A 5-foot-diameter sphere of fire appears in an unoccupied space of your choice within range and lasts for the duration. Any creature that ends its turn within 5 feet of the sphere must make a Dexterity saving throw. The creature takes 2d6 fire damage on a failed save, or half as much damage on a successful one. As a bonus action, you can move the sphere up to 30 feet.",
    higherLevel: "When you cast this spell using a spell slot of 3rd level or higher, the damage increases by 1d6 for each slot level above 2nd.",
    classes: ['druid', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'gentle-repose',
    name: 'Gentle Repose',
    level: 2,
    school: 'necromancy',
    ritual: true,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a pinch of salt and one copper piece placed on each of the corpse\'s eyes, which must remain there for the duration' },
    duration: '10 days',
    concentration: false,
    description: "You touch a corpse or other remains. For the duration, the target is protected from decay and can't become undead. The spell also effectively extends the time limit on raising the target from the dead, since days spent under the influence of this spell don't count against the time limit of spells such as raise dead.",
    classes: ['cleric', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'gust-of-wind',
    name: 'Gust of Wind',
    level: 2,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: 'Self (60-foot line)',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a legume seed' },
    duration: '1 minute',
    concentration: true,
    description: "A line of strong wind 60 feet long and 10 feet wide blasts from you in a direction you choose for the spell's duration. Each creature that starts its turn in the line must succeed on a Strength saving throw or be pushed 15 feet away from you in a direction following the line. Any creature in the line must spend 2 feet of movement for every 1 foot it moves when moving closer to you.",
    classes: ['druid', 'sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'heat-metal',
    name: 'Heat Metal',
    level: 2,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a piece of iron and a flame' },
    duration: '1 minute',
    concentration: true,
    description: "Choose a manufactured metal object, such as a metal weapon or a suit of heavy or medium metal armor, that you can see within range. You cause the object to glow red-hot. Any creature in physical contact with the object takes 2d8 fire damage when you cast the spell. Until the spell ends, you can use a bonus action on each of your subsequent turns to cause this damage again.",
    higherLevel: "When you cast this spell using a spell slot of 3rd level or higher, the damage increases by 1d8 for each slot level above 2nd.",
    classes: ['bard', 'druid'],
    source: 'srd-5.1',
  },
  {
    id: 'hold-person',
    name: 'Hold Person',
    level: 2,
    school: 'enchantment',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a small, straight piece of iron' },
    duration: '1 minute',
    concentration: true,
    description: "Choose a humanoid that you can see within range. The target must succeed on a Wisdom saving throw or be paralyzed for the duration. At the end of each of its turns, the target can make another Wisdom saving throw. On a success, the spell ends on the target.",
    higherLevel: "When you cast this spell using a spell slot of 3rd level or higher, you can target one additional humanoid for each slot level above 2nd. The humanoids must be within 30 feet of each other when you target them.",
    classes: ['bard', 'cleric', 'druid', 'sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'invisibility',
    name: 'Invisibility',
    level: 2,
    school: 'illusion',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'an eyelash encased in gum arabic' },
    duration: '1 hour',
    concentration: true,
    description: "A creature you touch becomes invisible until the spell ends. Anything the target is wearing or carrying is invisible as long as it is on the target's person. The spell ends for a target that attacks or casts a spell.",
    higherLevel: "When you cast this spell using a spell slot of 3rd level or higher, you can target one additional creature for each slot level above 2nd.",
    classes: ['bard', 'sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'knock',
    name: 'Knock',
    level: 2,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: false, material: false },
    duration: 'Instantaneous',
    concentration: false,
    description: "Choose an object that you can see within range. The object can be a door, a box, a chest, a set of manacles, a padlock, or another object that contains a mundane or magical means that prevents access. A target that is held shut by a mundane lock or that is stuck or barred becomes unlocked, unstuck, or unbarred. If the object has multiple locks, only one of them is unlocked. If you choose a target that is held shut with arcane lock, that spell is suppressed for 10 minutes.",
    classes: ['bard', 'sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'lesser-restoration',
    name: 'Lesser Restoration',
    level: 2,
    school: 'abjuration',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: false },
    duration: 'Instantaneous',
    concentration: false,
    description: "You touch a creature and can end either one disease or one condition afflicting it. The condition can be blinded, deafened, paralyzed, or poisoned.",
    classes: ['bard', 'cleric', 'druid', 'paladin', 'ranger'],
    source: 'srd-5.1',
  },
  {
    id: 'levitate',
    name: 'Levitate',
    level: 2,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'either a small leather loop or a piece of golden wire bent into a cup shape with a long shank on one end' },
    duration: '10 minutes',
    concentration: true,
    description: "One creature or object of your choice that you can see within range rises vertically, up to 20 feet, and remains suspended there for the duration. The spell can levitate a target that weighs up to 500 pounds. An unwilling creature that succeeds on a Constitution saving throw is unaffected. You can change the target's altitude by up to 20 feet in either direction on your turn.",
    classes: ['sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'locate-animals-or-plants',
    name: 'Locate Animals or Plants',
    level: 2,
    school: 'divination',
    ritual: true,
    castingTime: '1 action',
    range: 'Self',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a bit of fur from a bloodhound' },
    duration: 'Instantaneous',
    concentration: false,
    description: "Describe or name a specific kind of beast or plant. Concentrating on the voice of nature in your surroundings, you learn the direction and distance to the closest creature or plant of that kind within 5 miles, if any are present.",
    classes: ['bard', 'druid', 'ranger'],
    source: 'srd-5.1',
  },
  {
    id: 'locate-object',
    name: 'Locate Object',
    level: 2,
    school: 'divination',
    ritual: false,
    castingTime: '1 action',
    range: 'Self',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a forked twig' },
    duration: '10 minutes',
    concentration: true,
    description: "Describe or name an object that is familiar to you. You sense the direction to the object's location, as long as that object is within 1,000 feet of you. If the object is in motion, you know the direction of its movement. The spell can locate a specific object known to you, as long as you have seen it up close—within 30 feet—at least once.",
    classes: ['bard', 'cleric', 'druid', 'paladin', 'ranger', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'magic-mouth',
    name: 'Magic Mouth',
    level: 2,
    school: 'illusion',
    ritual: true,
    castingTime: '1 minute',
    range: '30 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a small bit of honeycomb and jade dust worth at least 10 gp, which the spell consumes', materialCost: 10, materialConsumed: true },
    duration: 'Until dispelled',
    concentration: false,
    description: "You implant a message within an object in range, a message that is uttered when a trigger condition is met. Choose an object that you can see and that isn't being worn or carried by another creature. Then speak the message, which must be 25 words or less, though it can be delivered over as long as 10 minutes. Finally, determine the circumstance that will trigger the spell to deliver your message.",
    classes: ['bard', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'magic-weapon',
    name: 'Magic Weapon',
    level: 2,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 bonus action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 hour',
    concentration: true,
    description: "You touch a nonmagical weapon. Until the spell ends, that weapon becomes a magic weapon with a +1 bonus to attack rolls and damage rolls.",
    higherLevel: "When you cast this spell using a spell slot of 4th level or higher, the bonus increases to +2. When you use a spell slot of 6th level or higher, the bonus increases to +3.",
    classes: ['paladin', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'mirror-image',
    name: 'Mirror Image',
    level: 2,
    school: 'illusion',
    ritual: false,
    castingTime: '1 action',
    range: 'Self',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 minute',
    concentration: false,
    description: "Three illusory duplicates of yourself appear in your space. Until the spell ends, the duplicates move with you and mimic your actions, shifting position so it's impossible to track which image is real. You can use your action to dismiss the illusory duplicates. Each time a creature targets you with an attack during the spell's duration, roll a d20 to determine whether the attack instead targets one of your duplicates.",
    classes: ['sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'misty-step',
    name: 'Misty Step',
    level: 2,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 bonus action',
    range: 'Self',
    components: { verbal: true, somatic: false, material: false },
    duration: 'Instantaneous',
    concentration: false,
    description: "Briefly surrounded by silvery mist, you teleport up to 30 feet to an unoccupied space that you can see.",
    classes: ['sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'moonbeam',
    name: 'Moonbeam',
    level: 2,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: '120 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'several seeds of any moonseed plant and a piece of opalescent feldspar' },
    duration: '1 minute',
    concentration: true,
    description: "A silvery beam of pale light shines down in a 5-foot-radius, 40-foot-high cylinder centered on a point within range. Until the spell ends, dim light fills the cylinder. When a creature enters the spell's area for the first time on a turn or starts its turn there, it is engulfed in ghostly flames that cause searing pain, and it must make a Constitution saving throw. It takes 2d10 radiant damage on a failed save, or half as much damage on a successful one.",
    higherLevel: "When you cast this spell using a spell slot of 3rd level or higher, the damage increases by 1d10 for each slot level above 2nd.",
    classes: ['druid'],
    source: 'srd-5.1',
  },
  {
    id: 'pass-without-trace',
    name: 'Pass without Trace',
    level: 2,
    school: 'abjuration',
    ritual: false,
    castingTime: '1 action',
    range: 'Self',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'ashes from a burned leaf of mistletoe and a sprig of spruce' },
    duration: '1 hour',
    concentration: true,
    description: "A veil of shadows and silence radiates from you, masking you and your companions from detection. For the duration, each creature you choose within 30 feet of you (including you) has a +10 bonus to Dexterity (Stealth) checks and can't be tracked except by magical means. A creature that receives this bonus leaves behind no tracks or other traces of its passage.",
    classes: ['druid', 'ranger'],
    source: 'srd-5.1',
  },
  {
    id: 'phantasmal-force',
    name: 'Phantasmal Force',
    level: 2,
    school: 'illusion',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a bit of fleece' },
    duration: '1 minute',
    concentration: true,
    description: "You craft an illusion that takes root in the mind of a creature that you can see within range. The target must make an Intelligence saving throw. On a failed save, you create a phantasmal object, creature, or other visible phenomenon of your choice that is no larger than a 10-foot cube and that is perceivable only to the target for the duration.",
    classes: ['bard', 'sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'prayer-of-healing',
    name: 'Prayer of Healing',
    level: 2,
    school: 'evocation',
    ritual: false,
    castingTime: '10 minutes',
    range: '30 feet',
    components: { verbal: true, somatic: false, material: false },
    duration: 'Instantaneous',
    concentration: false,
    description: "Up to six creatures of your choice that you can see within range each regain hit points equal to 2d8 + your spellcasting ability modifier. This spell has no effect on undead or constructs.",
    higherLevel: "When you cast this spell using a spell slot of 3rd level or higher, the healing increases by 1d8 for each slot level above 2nd.",
    classes: ['cleric'],
    source: 'srd-5.1',
  },
  {
    id: 'protection-from-poison',
    name: 'Protection from Poison',
    level: 2,
    school: 'abjuration',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 hour',
    concentration: false,
    description: "You touch a creature. If it is poisoned, you neutralize the poison. If more than one poison afflicts the target, you neutralize one poison that you know is present, or you neutralize one at random. For the duration, the target has advantage on saving throws against being poisoned, and it has resistance to poison damage.",
    classes: ['cleric', 'druid', 'paladin', 'ranger'],
    source: 'srd-5.1',
  },
  {
    id: 'ray-of-enfeeblement',
    name: 'Ray of Enfeeblement',
    level: 2,
    school: 'necromancy',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 minute',
    concentration: true,
    description: "A black beam of enervating energy springs from your finger toward a creature within range. Make a ranged spell attack against the target. On a hit, the target deals only half damage with weapon attacks that use Strength until the spell ends. At the end of each of the target's turns, it can make a Constitution saving throw against the spell. On a success, the spell ends.",
    classes: ['warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'rope-trick',
    name: 'Rope Trick',
    level: 2,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'powdered corn extract and a twisted loop of parchment' },
    duration: '1 hour',
    concentration: false,
    description: "You touch a length of rope that is up to 60 feet long. One end of the rope then rises into the air until the whole rope hangs perpendicular to the ground. At the upper end of the rope, an invisible entrance opens to an extradimensional space that lasts until the spell ends. The extradimensional space can be reached by climbing to the top of the rope.",
    classes: ['wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'scorching-ray',
    name: 'Scorching Ray',
    level: 2,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: '120 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: 'Instantaneous',
    concentration: false,
    description: "You create three rays of fire and hurl them at targets within range. You can hurl them at one target or several. Make a ranged spell attack for each ray. On a hit, the target takes 2d6 fire damage.",
    higherLevel: "When you cast this spell using a spell slot of 3rd level or higher, you create one additional ray for each slot level above 2nd.",
    classes: ['sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'see-invisibility',
    name: 'See Invisibility',
    level: 2,
    school: 'divination',
    ritual: false,
    castingTime: '1 action',
    range: 'Self',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a pinch of talc and a small sprinkling of powdered silver' },
    duration: '1 hour',
    concentration: false,
    description: "For the duration, you see invisible creatures and objects as if they were visible, and you can see into the Ethereal Plane. Ethereal creatures and objects appear ghostly and translucent.",
    classes: ['bard', 'sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'shatter',
    name: 'Shatter',
    level: 2,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a chip of mica' },
    duration: 'Instantaneous',
    concentration: false,
    description: "A sudden loud ringing noise, painfully intense, erupts from a point of your choice within range. Each creature in a 10-foot-radius sphere centered on that point must make a Constitution saving throw. A creature takes 3d8 thunder damage on a failed save, or half as much damage on a successful one. A creature made of inorganic material such as stone, crystal, or metal has disadvantage on this saving throw.",
    higherLevel: "When you cast this spell using a spell slot of 3rd level or higher, the damage increases by 1d8 for each slot level above 2nd.",
    classes: ['bard', 'sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'silence',
    name: 'Silence',
    level: 2,
    school: 'illusion',
    ritual: true,
    castingTime: '1 action',
    range: '120 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '10 minutes',
    concentration: true,
    description: "For the duration, no sound can be created within or pass through a 20-foot-radius sphere centered on a point you choose within range. Any creature or object entirely inside the sphere is immune to thunder damage, and creatures are deafened while entirely inside it. Casting a spell that includes a verbal component is impossible there.",
    classes: ['bard', 'cleric', 'ranger'],
    source: 'srd-5.1',
  },
  {
    id: 'spider-climb',
    name: 'Spider Climb',
    level: 2,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a drop of bitumen and a spider' },
    duration: '1 hour',
    concentration: true,
    description: "Until the spell ends, one willing creature you touch gains the ability to move up, down, and across vertical surfaces and upside down along ceilings, while leaving its hands free. The target also gains a climbing speed equal to its walking speed.",
    classes: ['sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'spike-growth',
    name: 'Spike Growth',
    level: 2,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: '150 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'seven sharp thorns or seven small twigs, each sharpened to a point' },
    duration: '10 minutes',
    concentration: true,
    description: "The ground in a 20-foot radius centered on a point within range twists and sprouts hard spikes and thorns. The area becomes difficult terrain for the duration. When a creature moves into or within the area, it takes 2d4 piercing damage for every 5 feet it travels. The transformation of the ground is camouflaged to look natural.",
    classes: ['druid', 'ranger'],
    source: 'srd-5.1',
  },
  {
    id: 'spiritual-weapon',
    name: 'Spiritual Weapon',
    level: 2,
    school: 'evocation',
    ritual: false,
    castingTime: '1 bonus action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 minute',
    concentration: false,
    description: "You create a floating, spectral weapon within range that lasts for the duration or until you cast this spell again. When you cast the spell, you can make a melee spell attack against a creature within 5 feet of the weapon. On a hit, the target takes force damage equal to 1d8 + your spellcasting ability modifier. As a bonus action on your turn, you can move the weapon up to 20 feet and repeat the attack against a creature within 5 feet of it.",
    higherLevel: "When you cast this spell using a spell slot of 3rd level or higher, the damage increases by 1d8 for every two slot levels above 2nd.",
    classes: ['cleric'],
    source: 'srd-5.1',
  },
  {
    id: 'suggestion',
    name: 'Suggestion',
    level: 2,
    school: 'enchantment',
    ritual: false,
    castingTime: '1 action',
    range: '30 feet',
    components: { verbal: true, somatic: false, material: true, materialDescription: 'a snake\'s tongue and either a bit of honeycomb or a drop of sweet oil' },
    duration: '8 hours',
    concentration: true,
    description: "You suggest a course of activity (limited to a sentence or two) and magically influence a creature you can see within range that can hear and understand you. Creatures that can't be charmed are immune to this effect. The suggestion must be worded in such a manner as to make the course of action sound reasonable. Asking the creature to stab itself, throw itself onto a spear, immolate itself, or do some other obviously harmful act ends the spell.",
    classes: ['bard', 'sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'warding-bond',
    name: 'Warding Bond',
    level: 2,
    school: 'abjuration',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a pair of platinum rings worth at least 50 gp each, which you and the target must wear for the duration', materialCost: 50 },
    duration: '1 hour',
    concentration: false,
    description: "This spell wards a willing creature you touch and creates a mystic connection between you and the target until the spell ends. While the target is within 60 feet of you, it gains a +1 bonus to AC and saving throws, and it has resistance to all damage. Also, each time it takes damage, you take the same amount of damage.",
    classes: ['cleric'],
    source: 'srd-5.1',
  },
  {
    id: 'web',
    name: 'Web',
    level: 2,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a bit of spiderweb' },
    duration: '1 hour',
    concentration: true,
    description: "You conjure a mass of thick, sticky webbing at a point of your choice within range. The webs fill a 20-foot cube from that point for the duration. The webs are difficult terrain and lightly obscure their area. If the webs aren't anchored between two solid masses (such as walls or trees) or layered across a floor, wall, or ceiling, the conjured web collapses on itself, and the spell ends at the start of your next turn.",
    classes: ['sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'zone-of-truth',
    name: 'Zone of Truth',
    level: 2,
    school: 'enchantment',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '10 minutes',
    concentration: false,
    description: "You create a magical zone that guards against deception in a 15-foot-radius sphere centered on a point of your choice within range. Until the spell ends, a creature that enters the spell's area for the first time on a turn or starts its turn there must make a Charisma saving throw. On a failed save, a creature can't speak a deliberate lie while in the radius. You know whether each creature succeeds or fails on its saving throw.",
    classes: ['bard', 'cleric', 'paladin'],
    source: 'srd-5.1',
  },
];
